/**
 * ============================================================================
 * DURATION RANGE — builds the absolute week tokens for getMetrics.
 * ============================================================================
 *
 * The getMetrics contract takes absolute Canonical Week Tokens
 * ("<year> Week <NN>", zero-padded — e.g. "2026 Week 04"), not relative
 * offsets. The week universe comes from getWeekNumbers (-55..+4 around the
 * current week) already in that format; this module slices a window out of
 * that universe so week arithmetic — including year boundaries — never
 * happens on the client.
 */

/**
 * Normalize any supported week format to the Canonical Week Token getMetrics
 * validates: "2026-WK7" | "2026 Week 7" → "2026 Week 07" (NN zero-padded).
 * Compact tokens ("2026Week30") are REJECTED by the backend — never send them.
 */
export function toCanonicalWeekToken(week: string): string {
  const match = week.match(/^(\d{4})[\s-]*(?:WK|Week)\s*(\d+)$/i);
  if (!match) return week;
  return `${match[1]} Week ${match[2].padStart(2, '0')}`;
}

/** Format any supported week token for display: "2026 Week 30". */
export function toWeekLabel(week: string): string {
  const match = week.match(/^(\d{4})[\s-]*(?:WK|Week)\s*(\d+)$/i);
  if (!match) return week;
  return `${match[1]} Week ${parseInt(match[2], 10)}`;
}

/**
 * The week immediately before `week` in the universe. Used as the snapshot
 * anchor: a snapshot is the frozen WBR capture of COMPLETED weeks, so its
 * window ends at the last completed week, not the running one.
 */
export function weekBefore(weeks: string[], week: string): string {
  const index = weeks.indexOf(week);
  return index > 0 ? weeks[index - 1] : week;
}

/** Week-control defaults — MUST match dashboardSlice initial state. */
export const DEFAULT_LAST_X_WEEKS = 4;
export const DEFAULT_FORWARD_X_WEEKS = 4;

/**
 * True when the week controls are untouched. Windows are always sent
 * explicitly now (weeks are cached individually, so pages share weeks rather
 * than sharing one whole-window entry), so this no longer decides whether to
 * omit the period list — it just reports whether the user has customised the
 * window. Snapshot mode pins forwardXWeeks to 0 (dataModeChanged), so 0 IS the
 * snapshot default.
 */
export function isDefaultWindow(
  reportingWeek: string | null,
  lastX: number,
  forwardX: number,
  isSnapshot: boolean,
): boolean {
  return (
    reportingWeek === null &&
    lastX === DEFAULT_LAST_X_WEEKS &&
    forwardX === (isSnapshot ? 0 : DEFAULT_FORWARD_X_WEEKS)
  );
}

/**
 * The home page's window: the historical weeks plus the anchor week (R4.1).
 * Forward weeks are excluded because selectWbrDashboardData drops them — asking
 * for them would fetch ~4 weeks of data no card renders.
 *
 * In snapshot mode the anchor is the last COMPLETED week, matching tilesWindow:
 * a snapshot is the capture taken when a week closes, so the running week has
 * none by definition and asking for it wastes a request on an empty period.
 */
export function homeWindow(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  isSnapshot: boolean,
  lastX: number = DEFAULT_LAST_X_WEEKS,
): string[] | undefined {
  if (!weeks?.length || !currentWeek) return undefined;
  const anchor = isSnapshot ? weekBefore(weeks, currentWeek) : currentWeek;
  const anchorIndex = weeks.indexOf(anchor);
  if (anchorIndex === -1) return undefined;
  return weeks.slice(Math.max(0, anchorIndex - lastX), anchorIndex + 1).map(toCanonicalWeekToken);
}

/**
 * The tiles page's window: exactly ONE week (R4.2) — the running week in live
 * mode, the last COMPLETED week in snapshot (a capture is taken at week close).
 */
export function tilesWindow(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  isSnapshot: boolean,
): string[] | undefined {
  if (!weeks?.length || !currentWeek) return undefined;
  const anchor = isSnapshot ? weekBefore(weeks, currentWeek) : currentWeek;
  return weeks.includes(anchor) ? [toCanonicalWeekToken(anchor)] : undefined;
}

export interface DurationWindow {
  /** Anchor week (defaults to currentWeek when no reporting week chosen). */
  anchor: string;
  /** Absolute tokens for getMetrics, oldest first. */
  durationRange: string[];
}

/**
 * Slice a window of `lastX` weeks before and `forwardX` weeks after the
 * anchor out of the getWeekNumbers universe.
 *
 * @param weeks       Ordered week list from getWeekNumbers (oldest first).
 * @param currentWeek Current week from getWeekNumbers.
 * @param reportingWeek Chosen anchor; null → currentWeek.
 * @param lastX       Weeks before the anchor.
 * @param forwardX    Weeks after the anchor (0 in snapshot mode).
 * @returns null while the week universe hasn't loaded yet.
 */
export function buildDurationWindow(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  reportingWeek: string | null,
  lastX: number,
  forwardX: number,
): DurationWindow | null {
  if (!weeks?.length || !currentWeek) return null;

  const anchor = reportingWeek && weeks.includes(reportingWeek) ? reportingWeek : currentWeek;
  const anchorIndex = weeks.indexOf(anchor);
  if (anchorIndex === -1) return null;

  const start = Math.max(0, anchorIndex - lastX);
  const end = Math.min(weeks.length - 1, anchorIndex + forwardX);
  return {
    anchor,
    durationRange: weeks.slice(start, end + 1).map(toCanonicalWeekToken),
  };
}
